/**
 * <video-pause> — shared Web Component for looping video play/pause controls.
 *
 * Wraps one media slot (one video element or a desktop/mobile pair). Wires
 * .video-pause-btn to all <video> elements inside itself: click toggles all
 * videos together (desktop + mobile pair play/pause as a unit), and the
 * play/pause events on any video sync the button's aria-pressed and aria-label.
 *
 * Works alongside hero-sticky.js, which handles its own bespoke button wiring.
 */
class VideoPause extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.#wire();
  }

  #wire() {
    const btn = this.querySelector('.video-pause-btn');
    if (!btn) return;

    const bind = () => {
      const videos = [...this.querySelectorAll('video')];
      if (!videos.length) return;

      const syncBtn = () => {
        const playing = videos.some((v) => !v.paused);
        btn.setAttribute('aria-pressed', playing ? 'false' : 'true');
        btn.setAttribute(
          'aria-label',
          playing ? btn.dataset.pauseLabel : btn.dataset.playLabel
        );
      };

      btn.addEventListener('click', () => {
        const playing = videos.some((v) => !v.paused);
        videos.forEach((v) => {
          if (playing) {
            v.pause();
          } else {
            const p = v.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          }
        });
      });

      videos.forEach((v) => {
        v.addEventListener('play', syncBtn);
        v.addEventListener('pause', syncBtn);
      });

      syncBtn();
    };

    if (this.querySelector('video')) {
      bind();
    } else {
      // Lazily-injected video (e.g. deferred-media) — wait for it.
      const mo = new MutationObserver(() => {
        if (this.querySelector('video')) {
          mo.disconnect();
          bind();
        }
      });
      mo.observe(this, { childList: true, subtree: true });
    }
  }
}

if (!customElements.get('video-pause')) {
  customElements.define('video-pause', VideoPause);
}
